/**
 * Stable device fingerprint.
 *
 * Combines several browser signals into a SHA-256-style hash that survives
 * cookie/localStorage clears (what a multi-account farmer usually does).
 * Sent with login / signup requests via the `X-Device-Fingerprint` header;
 * the backend uses it to enforce the 2-accounts-per-device cap.
 *
 * Signals:
 *   - User agent
 *   - Platform / vendor / language / timezone
 *   - Screen resolution + colour depth
 *   - Hardware concurrency + deviceMemory (if exposed)
 *   - Canvas rendering hash (GPU + antialiasing)
 *   - WebGL vendor + renderer (GPU signature)
 *   - Audio context signature
 *
 * Cached in localStorage so the same device always returns the same value
 * (even if the user clears cookies — localStorage survives that in most
 * browsers). If localStorage is wiped, the recomputed hash still matches
 * because the underlying signals are unchanged.
 */

const STORAGE_KEY = "gv_device_fp_v2";
const DEVICE_ID_STORAGE_KEY = "gv_device_id_v1";
const DEVICE_COOKIE_NAME = "nexdai_device_id";

function randomDeviceId() {
  const browserCrypto =
    typeof globalThis !== "undefined" ? globalThis.crypto : null;
  if (browserCrypto?.randomUUID) return browserCrypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (browserCrypto?.getRandomValues) {
    browserCrypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function getCookie(name) {
  try {
    const prefix = `${name}=`;
    const found = document.cookie
      .split(";")
      .map((part) => part.trim())
      .find((part) => part.startsWith(prefix));
    return found ? decodeURIComponent(found.slice(prefix.length)) : "";
  } catch {
    return "";
  }
}

function setDeviceCookie(value) {
  try {
    document.cookie = `${DEVICE_COOKIE_NAME}=${encodeURIComponent(
      value,
    )}; Max-Age=${60 * 60 * 24 * 400}; Path=/; SameSite=Lax; Secure`;
  } catch {
    /* cookies blocked */
  }
}

async function sha256Hex(input) {
  const buffer = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function canvasSignature() {
  try {
    const c = document.createElement("canvas");
    c.width = 220;
    c.height = 40;
    const ctx = c.getContext("2d");
    if (!ctx) return "no-2d";
    ctx.textBaseline = "top";
    ctx.font = "14px 'Arial'";
    ctx.fillStyle = "#f60";
    ctx.fillRect(100, 5, 80, 20);
    ctx.fillStyle = "#069";
    ctx.fillText("game-vault-fp", 2, 2);
    ctx.strokeStyle = "rgba(102,204,0,0.8)";
    ctx.arc(55, 20, 8, 0, Math.PI * 2, true);
    ctx.stroke();
    return c.toDataURL();
  } catch {
    return "canvas-blocked";
  }
}

function webglSignature() {
  try {
    const c = document.createElement("canvas");
    const gl = c.getContext("webgl") || c.getContext("experimental-webgl");
    if (!gl) return "no-webgl";
    const ext = gl.getExtension("WEBGL_debug_renderer_info");
    const vendor = ext
      ? gl.getParameter(ext.UNMASKED_VENDOR_WEBGL)
      : gl.getParameter(gl.VENDOR);
    const renderer = ext
      ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL)
      : gl.getParameter(gl.RENDERER);
    return `${vendor}|${renderer}`;
  } catch {
    return "webgl-blocked";
  }
}

function audioSignature() {
  try {
    const AC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    if (!AC) return "no-audio";
    const ac = new AC(1, 44100, 44100);
    const osc = ac.createOscillator();
    osc.type = "triangle";
    osc.frequency.value = 10000;
    const comp = ac.createDynamicsCompressor();
    osc.connect(comp);
    comp.connect(ac.destination);
    osc.start(0);
    return `${ac.sampleRate}|${comp.threshold.value}|${comp.knee.value}`;
  } catch {
    return "audio-blocked";
  }
}

async function computeFingerprint() {
  const pluginSignature = (() => {
    try {
      return Array.from(navigator.plugins || [])
        .slice(0, 20)
        .map((p) => `${p.name}:${p.filename}:${p.length}`)
        .join(",");
    } catch {
      return "plugins-blocked";
    }
  })();
  const mimeSignature = (() => {
    try {
      return Array.from(navigator.mimeTypes || [])
        .slice(0, 30)
        .map((m) => `${m.type}:${m.suffixes}`)
        .join(",");
    } catch {
      return "mimes-blocked";
    }
  })();
  const mediaSignature = (() => {
    try {
      return [
        matchMedia("(pointer: coarse)").matches ? "coarse" : "fine",
        matchMedia("(hover: hover)").matches ? "hover" : "no-hover",
        matchMedia("(color-gamut: p3)").matches ? "p3" : "srgb",
        matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "reduced-motion"
          : "motion-ok",
      ].join("|");
    } catch {
      return "media-blocked";
    }
  })();
  const parts = [
    navigator.userAgent || "",
    navigator.platform || "",
    navigator.vendor || "",
    navigator.language || "",
    (navigator.languages || []).join(","),
    `${navigator.maxTouchPoints || 0}`,
    `${navigator.webdriver ? "webdriver" : "human"}`,
    `${navigator.cookieEnabled ? "cookies" : "no-cookies"}`,
    Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    `${new Date().getTimezoneOffset()}`,
    `${screen.width}x${screen.height}x${screen.colorDepth}`,
    `${screen.availWidth}x${screen.availHeight}`,
    `${navigator.hardwareConcurrency || ""}`,
    `${navigator.deviceMemory || ""}`,
    `${screen.pixelDepth || ""}`,
    `${window.devicePixelRatio || ""}`,
    pluginSignature,
    mimeSignature,
    mediaSignature,
    canvasSignature(),
    webglSignature(),
    audioSignature(),
  ].join("||");
  return sha256Hex(parts);
}

let cached = null;
let cachedDeviceId = null;

export function getDeviceId() {
  if (cachedDeviceId) return cachedDeviceId;
  let value = "";
  try {
    value = localStorage.getItem(DEVICE_ID_STORAGE_KEY) || "";
  } catch {
    value = "";
  }
  if (!value) value = getCookie(DEVICE_COOKIE_NAME);
  if (!value) value = randomDeviceId();
  cachedDeviceId = value;
  try {
    localStorage.setItem(DEVICE_ID_STORAGE_KEY, value);
  } catch {
    /* storage blocked */
  }
  setDeviceCookie(value);
  return value;
}

export async function getDeviceFingerprint() {
  if (cached) return cached;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && stored.length === 64) {
      cached = stored;
      return cached;
    }
  } catch {
    /* storage blocked */
  }
  const fp = await computeFingerprint();
  try {
    localStorage.setItem(STORAGE_KEY, fp);
  } catch {
    /* ignore */
  }
  cached = fp;
  return cached;
}

/**
 * Convenience helper for axios/fetch: returns a header object with the
 * fingerprint already resolved.
 */
export async function deviceFingerprintHeaders() {
  const fp = await getDeviceFingerprint();
  const headers = {
    "X-Device-Fingerprint": fp,
    "X-Device-Id": getDeviceId(),
  };
  try {
    headers["X-Client-Timezone"] =
      Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    headers["X-Client-Timezone"] = "";
  }
  headers["X-Client-Language"] =
    navigator.language || (navigator.languages || [])[0] || "";
  return headers;
}
